import {HttpParams} from '@angular/common/http';
import {Component, EventEmitter, OnInit} from '@angular/core';
import {NbWindowService} from '@nebular/theme';
import {BaseFieldType, CatalogWithAncestors} from 'compob2b-libs';
import {Observable, of, pipe} from 'rxjs';
import {filter, map, switchMap, takeUntil, tap} from 'rxjs/operators';
import {translations} from '../../../../environments/translations.ru';
import {ChooseCatalogModalComponent} from '../../choose-catalog-modal/choose-catalog-modal.component';
import {CatalogService} from '../../../cabinet/pim/catalog/catalog.service'

@Component({
  selector: 'app-catalog-picker-multi',
  templateUrl: './catalog-picker-multi.component.html',
  styleUrls: ['./catalog-picker-multi.component.css'],
  standalone: false
})
export class CatalogPickerMultiComponent extends BaseFieldType implements OnInit {
  catalogs: CatalogWithAncestors[] = [];
  catalogChosenEvent: EventEmitter<CatalogWithAncestors> = new EventEmitter<CatalogWithAncestors>();
  catalogRemoveEvent: EventEmitter<CatalogWithAncestors> = new EventEmitter<CatalogWithAncestors>();

  constructor(
    protected readonly catalogService: CatalogService,
    protected readonly windowService: NbWindowService
  ) {
    super();
  }

  ngOnInit(): void {
    this.formControl.valueChanges
      .pipe(
        pipe(takeUntil(this.unsubscribe)),
        tap(ids => this.clearCatalogs(ids)),
        filter(ids => Array.isArray(ids) && ids.length > 0),
        switchMap(ids => this.getCatalogsWithAncestorsByIDs(ids))
      )
      .subscribe(catalogs => {
        this.catalogs = catalogs;
        this.cdr.detectChanges();
      }, error => this.handleError(error));

    this.catalogChosenEvent.pipe(pipe(takeUntil(this.unsubscribe))).subscribe(catalog => this.onCatalogSelected(catalog));
    this.catalogRemoveEvent.pipe(pipe(takeUntil(this.unsubscribe))).subscribe(catalog => this.onCatalogRemoved(catalog));
  }

  getCatalogsWithAncestorsByIDs(catalogIDs: number[]): Observable<CatalogWithAncestors[]> {
    if (this.catalogs && this.arraysEqual(this.catalogs.map(item => item.id), catalogIDs)) {
      return of(this.catalogs);
    }
    const params = new HttpParams().set('ids', JSON.stringify(catalogIDs));

    this.loading = true;
    return this.compoApiService.getWithParams<CatalogWithAncestors[]>(`/${this.catalogService.apiUrl}/with-ancestors-for-ids`, params)
      .pipe(
        map(response => response.data),
        tap(_ => this.loading = false),
      );
  }

  openChooseCatalogModal(): void {
    this.formControl.markAsTouched();

    this.windowService.open(ChooseCatalogModalComponent, {
      context: {
        catalogChosenEvent: this.catalogChosenEvent,
        removeCatalogEvent: this.catalogRemoveEvent,
        onlyOneSelection: false,
        chooseOnlyLastLevel: true,
        fullChosenCatalogs: this.catalogs.slice(),
        catalogService: this.catalogService,
      },
      title: translations.selectOneCatalog
    });
  }

  removeCatalogValue(catalog: CatalogWithAncestors): void {
    this.formControl.markAsTouched();
    const index: number = this.catalogs.findIndex(item => item.id === catalog.id);
    if (index === -1) {
      return;
    }
    this.catalogs.splice(index, 1);
    this.formControl.setValue(this.catalogs.map(item => item.id));
    this.cdr.detectChanges();
  }

  clearAllCatalogs(): void {
    this.formControl.markAsTouched();
    if (this.catalogs.length === 0) {
      return;
    }

    this.catalogs = [];
    this.setCatalogs();
  }

  getOptionLabel(catalog: CatalogWithAncestors): string {
    return catalog ? catalog.ancestors.filter(item => item !== null).concat(catalog.header).join(' > ') : '';
  }

  private setCatalogs(): void {
    this.formControl.setValue(this.catalogs.map(item => item.id));
    this.cdr.detectChanges();
  }

  private onCatalogSelected(chosenCatalog: CatalogWithAncestors): void {
    if (!chosenCatalog || (this.catalogs && this.catalogs.findIndex(item => item.id === chosenCatalog.id) !== -1)) {
      return;
    }

    this.catalogs.push(chosenCatalog);
    this.setCatalogs();
  }

  private onCatalogRemoved(catalog: CatalogWithAncestors): void {
    if (!catalog || !Array.isArray(this.catalogs) || this.catalogs.length === 0) {
      return;
    }

    const index: number = this.catalogs.findIndex(item => item.id === catalog.id);
    if (index === -1) {
      return;
    }

    this.catalogs.splice(index, 1);
    this.setCatalogs();
  }

  private clearCatalogs(catalogIDs: number[]): void {
    if (!catalogIDs || catalogIDs.length === 0) {
      this.catalogs = [];
    }
  }

  private arraysEqual(a: number[], b: number[]) {
    a = Array.isArray(a) ? a : [];
    b = Array.isArray(b) ? b : [];
    return a.length === b.length && a.every((el, ix) => el === b[ix]);
  }
}
